import type { ChangeEvent, FunctionComponent } from "react";
import { useCallback, useMemo, useRef } from "react";
import { graphql, useFragment } from "react-relay";
import "swiper/css";
import "swiper/css/navigation";
import { Navigation } from "swiper/modules";
import type { SwiperRef } from "swiper/react";
import { Swiper } from "swiper/react";

import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { videoUpNextWidgetVideo$key } from "scmp-app/queries/__generated__/videoUpNextWidgetVideo.graphql";

import { VideoUpNextWidgetItem } from "./item";
import {
  Container,
  Header,
  StyledArrowNext,
  StyledArrowPrevious,
  StyledSwiperSlide,
  StyledSwitch,
  SwiperWrapper,
  Title,
  ToggleContainer,
  ToggleLabel,
} from "./styles";

export type Props = {
  autoplayEnabled: boolean;
  className?: string;
  currentVideoId?: string;
  onSelect: (id: string) => void;
  onToggleAutoplay: (enabled: boolean) => void;
  reference: videoUpNextWidgetVideo$key;
};

export const VideoUpNextWidget: FunctionComponent<Props> = ({
  autoplayEnabled,
  className,
  currentVideoId,
  onSelect,
  onToggleAutoplay,
  reference,
}) => {
  const items = useFragment(
    graphql`
      fragment videoUpNextWidgetVideo on QueueItemsEdge @relay(plural: true) {
        node {
          ... on Video {
            entityId
            ...itemVideoUpNextWidgetItemVideo
          }
        }
      }
    `,
    reference,
  );

  const sortedItems = useMemo(() => {
    const newItems = [...items];
    const currentVideoIndex = newItems.findIndex(({ node }) => node.entityId === currentVideoId);
    const firstPart = newItems.slice(0, currentVideoIndex);
    const secondPart = newItems.slice(currentVideoIndex + 1);
    return [...secondPart, ...firstPart];
  }, [currentVideoId, items]);

  const handleToggleAutoplay = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const enabled = event.target.checked;
      sendGA4Tracking({
        action: "click",
        category: "video",
        customized_parameters: {
          switch: enabled ? "on" : "off",
          widget_name: "up_next_widget",
        },
        subcategory: "autoplay",
      });
      onToggleAutoplay(enabled);
    },
    [onToggleAutoplay],
  );

  const swiperReference = useRef<SwiperRef>(null);

  const handleSelectVideo = useCallback(
    (id: string) => {
      if (swiperReference.current) {
        swiperReference.current.swiper.slideTo(0);
      }
      onSelect(id);
    },
    [onSelect],
  );

  if (sortedItems.length === 0) return null;

  return (
    <Container className={className}>
      <Header>
        <Title>UP NEXT</Title>
        <ToggleContainer>
          <ToggleLabel>Autoplay</ToggleLabel>
          <StyledSwitch
            $enabled={autoplayEnabled}
            checked={autoplayEnabled}
            onChange={handleToggleAutoplay}
          />
        </ToggleContainer>
      </Header>

      <SwiperWrapper>
        <Swiper
          freeMode={true}
          modules={[Navigation]}
          navigation={{
            disabledClass: "card-swiper-button-disabled",
            nextEl: ".card-swiper-button-next",
            prevEl: ".card-swiper-button-prev",
          }}
          pagination={false}
          ref={swiperReference}
          slidesOffsetAfter={16}
          slidesOffsetBefore={16}
          slidesPerView="auto"
          spaceBetween={16}
        >
          {sortedItems.map(({ node }, index) => (
            <StyledSwiperSlide key={index}>
              <VideoUpNextWidgetItem onSelect={handleSelectVideo} reference={node} />
            </StyledSwiperSlide>
          ))}
        </Swiper>

        <div className="swiper-button card-swiper-button-prev">
          <StyledArrowPrevious />
        </div>
        <div className="swiper-button card-swiper-button-next">
          <StyledArrowNext />
        </div>
      </SwiperWrapper>
    </Container>
  );
};

VideoUpNextWidget.displayName = "VideoUpNextWidget";
